import {DashboardRepository} from "../service/cache/DashboardRepository";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {
    BuildDashboardSummary,
    DashboardConfiguration,
    DashboardSummaries,
    DashboardViewConfiguration,
    emptyDashboardConfiguration,
    LayoutWidgetConfiguration,
    WidgetOptions
} from "../models/DashboardViewConfiguration";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {ascendingUsing, isNotNull} from "../../../js/util/UtilFunctions";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {
    AddDashboardUpdate,
    addDashboardUpdate,
    dashboardSecurityUpdate,
    DashboardSecurityUpdate,
    DashboardStoreUpdate,
    DashboardWidgetRemove,
    dashboardWidgetRemove,
    dashboardWidgetUpdate,
    DashboardWidgetUpdate,
    isAddDashboardUpdate,
    isRemoveDashboardUpdate,
    isSecurityUpdate,
    isSyncUpdate,
    isWidgetRemove,
    isWidgetUpdate,
    RemoveDashboardUpdate,
    removeDashboardUpdate
} from "./models/DashboardStoreUpdate";
import {CodeAndExchange} from "app/instruments/types/CodeAndExchange";
import {WidgetType} from "../models/WidgetType";
import {CurrentUser} from "../../../services/user/models/CurrentUser";
import {FeatureNames} from "../../../types/UserConfig";
import {AddDashboardConfiguration} from "../dialog/add-dashboard/models/AddDashboardConfiguration";
import {DefaultDashboardConfigurationService} from "../service/DefaultDashboardConfigurationService";
import {Optional} from "../../../js/util/Optional";
import {Branding} from "../../../types/Branding";

const ConfigUpdateIntervalSeconds = 2;
const ConfigUpdateInterval = ConfigUpdateIntervalSeconds * 1000;

export class DashboardViewStore {

    public static readonly id = "dashboardViewStore";

    private readonly currentDashboardNumber$ = new BehaviorSubject(1);
    private readonly configuration$ = new ReplaySubject<DashboardViewConfiguration>(1);
    private readonly updateConfiguration$ = new ReplaySubject<DashboardStoreUpdate>(1);
    private readonly availableWidgetTypes$: Observable<ReadonlyArray<WidgetType>>;

    private readonly dashboardSummaries$: Observable<DashboardSummaries>;
    private readonly currentDashboard$: Observable<DashboardConfiguration>;

    /** @ngInject */
    public constructor(private readonly dashboardRepository: DashboardRepository,
                       private readonly currentUserService: CurrentUserService,
                       private readonly defaultDashboardConfigurationService: DefaultDashboardConfigurationService) {
        this.dashboardSummaries$ = this.buildSummaries();
        this.currentDashboard$ = this.buildCurrentDashboard();
        this.availableWidgetTypes$ = this.buildAvailableWidgetTypes();

        this.watchUpdateConfiguration();
        this.init();
    }

    private init() {
        Observable.of(this.dashboardRepository)
                  .flatMap((r) => r.configuration())
                  .flatMap((c) => this.configurationOrDefault(c))
                  .subscribe((current) => this.watchUpdates(current));
    }

    private configurationOrDefault(config: Optional<DashboardViewConfiguration>): Promise<DashboardViewConfiguration> {
        return config.map((c) => Promise.resolve(c))
                     .orElseGet(() => this.defaultDashboardConfigurationService.forUser(this.currentUserService.user));
    }

    private watchUpdates(currentConfig: DashboardViewConfiguration) {
        this.updateConfiguration$
            .scan((acc, next) => this.mergeChange(acc, next), currentConfig)
            .startWith(currentConfig)
            .do((config) => console.debug("Current Dashboard Config", config))
            .subscribe((config) => this.configuration$.next(config));
    }

    private mergeChange(configuration: DashboardViewConfiguration,
                        update: DashboardStoreUpdate): DashboardViewConfiguration {
        if (isSecurityUpdate(update)) {
            return this.updateLayoutSecurity(configuration, update);
        } else if (isSyncUpdate(update)) {
            return update.configuration;
        } else if (isWidgetUpdate(update)) {
            return this.updateWidgetConfiguration(configuration, update);
        } else if (isWidgetRemove(update)) {
            return this.removeWidgetConfiguration(configuration, update);
        } else if (isAddDashboardUpdate(update)) {
            return this.addDashboardConfiguration(configuration, update);
        } else if (isRemoveDashboardUpdate(update)) {
            return this.removeDashboardConfiguration(configuration, update);
        } else {
            return configuration;
        }
    }

    private updateLayoutSecurity(configuration: DashboardViewConfiguration,
                                 update: DashboardSecurityUpdate): DashboardViewConfiguration {
        const dashboard = configuration[update.dashboard];
        const layout = dashboard.layout;
        const updatedLayout = {...layout, security: update.security};
        const updatedDashboard = {...dashboard, layout: updatedLayout};
        return {
            ...configuration,
            [update.dashboard]: updatedDashboard
        };
    }

    private updateWidgetConfiguration(configuration: DashboardViewConfiguration,
                                      update: DashboardWidgetUpdate): DashboardViewConfiguration {
        const dashboard = configuration[update.dashboard];
        const layout = dashboard.layout;
        const widgets = layout.widgets;
        const updatedWidgets = {...widgets, [update.widget]: update.options};
        const updatedLayout = {...layout, widgets: updatedWidgets};
        const updatedDashboard = {...dashboard, layout: updatedLayout};
        return {...configuration, [update.dashboard]: updatedDashboard};
    }

    private removeWidgetConfiguration(configuration: DashboardViewConfiguration,
                                      update: DashboardWidgetRemove): DashboardViewConfiguration {
        const dashboard = configuration[update.dashboard];
        const layout = dashboard.layout;
        const widgets = layout.widgets;
        // Split into specified property and everything else, essentially the same as "delete"
        // but leaving the original object unmodified
        const {[update.widget]: _, ...updatedWidgets} = widgets;
        const updatedLayout = {...layout, widgets: updatedWidgets};
        const updatedDashboard = {...dashboard, layout: updatedLayout};
        return {...configuration, [update.dashboard]: updatedDashboard};
    }

    private addDashboardConfiguration(configuration: DashboardViewConfiguration,
                                      update: AddDashboardUpdate): DashboardViewConfiguration {
        const dashboardNumber = Object.keys(configuration).length + 1;
        // This needs to happen after all the observables have updated
        setTimeout(() => this.currentDashboardNumber$.next(dashboardNumber));
        return {
            ...configuration,
            [dashboardNumber]: emptyDashboardConfiguration(update.name, update.layout)
        };
    }

    private removeDashboardConfiguration(configuration: DashboardViewConfiguration,
                                         update: RemoveDashboardUpdate): DashboardViewConfiguration {
        const {[update.dashboard]: _, ...remainingConfiguration} = configuration;
        // Re-Order the configurations to be back to indexes with no gaps
        return ObjectUtil.keys(remainingConfiguration)
                         .sort()
                         .map((k) => remainingConfiguration[k])
                         .reduce((acc, next, index) => Object.assign({}, acc, {[index + 1]: next}), {});
    }

    private buildSummaries(): Observable<DashboardSummaries> {
        return this.configuration$
                   .map((dashboards) => this.extractDashboardSummaries(dashboards))
                   .distinctUntilChanged((a, b) => this.summariesUnchanged(a, b))
                   .do((summaries) => console.debug("Current Summaries", summaries))
                   .publishReplay(1)
                   .refCount();
    }

    private extractDashboardSummaries(dashboards: DashboardViewConfiguration): DashboardSummaries {
        return ObjectUtil.keys(dashboards)
                         .map((key) => BuildDashboardSummary(+key, dashboards[key].name))
                         .sort(ascendingUsing((s) => s.order));
    }

    private summariesUnchanged(previous: DashboardSummaries, next: DashboardSummaries): boolean {
        return ArrayUtil.matches(previous, next, (a, b) => (a.name === b.name) && (a.order === b.order));
    }

    private buildCurrentDashboard(): Observable<DashboardConfiguration> {
        const selected$ = this.currentDashboardNumber$.filter(isNotNull);
        const source$ = Observable.combineLatest(this.configuration$, selected$);
        return source$.map(([config, selected]) => config[selected])
                      .filter(isNotNull)
                      .map((config) => this.removeUnusedWidgets(config))
                      .filter(isNotNull)
                      .distinctUntilChanged()
                      .publishReplay(1)
                      .refCount();
    }

    private removeUnusedWidgets(config: DashboardConfiguration): DashboardConfiguration {
        const layout = config.layout;
        const widgets = ObjectUtil.entries(layout.widgets)
                                  .filter(([key, options]) => this.isActiveWidget(options.type))
                                  .reduce((acc, [key, options]) => Object.assign({}, acc, {[key]: options}),
                                          {} as LayoutWidgetConfiguration);
        const updatedLayout = {...layout, widgets};
        return {...config, layout: updatedLayout};
    }

    private isActiveWidget(type: WidgetType): boolean {
        // return type !== WidgetType.NEWS;
        return true;
    }

    /* Persist the changes in the background */
    private watchUpdateConfiguration() {
        this.configuration$
            .auditTime(ConfigUpdateInterval)
            .subscribe((config) => this.dashboardRepository.updateConfiguration(config));
    }

    public get dashboards(): Observable<DashboardSummaries> {
        return this.dashboardSummaries$;
    }

    public get currentDashboard(): Observable<DashboardConfiguration> {
        return this.currentDashboard$;
    }

    public get configuration(): Observable<DashboardViewConfiguration> {
        return this.configuration$.asObservable();
    }

    public get currentDashboardNumber(): Observable<number> {
        return this.currentDashboardNumber$.distinctUntilChanged();
    }

    public select(index: number) {
        this.currentDashboardNumber$.next(index);
    }

    public updateSecurity(dashboard: number, security: CodeAndExchange) {
        const update = dashboardSecurityUpdate(security, dashboard);
        this.updateConfiguration$.next(update);
    }

    public updateWidget(dashboard: number, widget: number, options: WidgetOptions) {
        const update = dashboardWidgetUpdate(dashboard, widget, options);
        this.updateConfiguration$.next(update);
    }

    public removeWidget(dashboard: number, widget: number) {
        const update = dashboardWidgetRemove(dashboard, widget);
        this.updateConfiguration$.next(update);
    }

    public get availableWidgetTypes(): Observable<ReadonlyArray<WidgetType>> {
        return this.availableWidgetTypes$;
    }

    private buildAvailableWidgetTypes(): Observable<ReadonlyArray<WidgetType>> {

        const allWidgetTypes = Observable.of(WidgetType)
                                         .map(ObjectUtil.keys)
                                         .map((keys) => keys as ReadonlyArray<WidgetType>);

        return Observable.combineLatest(allWidgetTypes, this.currentUserService.current)
                         .map(([widgetTypes, currentUser]) => this.filterAvailableWidgetTypes(widgetTypes, currentUser))
                         .publishReplay(1)
                         .refCount();
    }

    private filterAvailableWidgetTypes(widgetTypes: ReadonlyArray<WidgetType>,
                                       currentUser: CurrentUser): ReadonlyArray<WidgetType> {
        return widgetTypes.filter((widgetType) => this.filterWidgetType(widgetType, currentUser));
    }

    private filterWidgetType(widgetType: WidgetType, currentUser: CurrentUser): boolean {
        switch (widgetType) {
            case WidgetType.TRADE_IDEAS:
                return currentUser.hasFeature(FeatureNames.LITT_TRADING_SYSTEM);
            case WidgetType.OPEN_TRADER_HELP:
                return currentUser.branding() === Branding.OPEN_TRADER;
            case WidgetType.NEWS:
                return currentUser.hasFeature(FeatureNames.NEWS);
            case WidgetType.ORDER_DEPTH:
                return currentUser.isLivePrices();
            case WidgetType.COURSE_OF_SALES:
                return currentUser.isLivePrices()
            default:
                return true;
        }
    }

    public addDashboard(dashboard: AddDashboardConfiguration) {
        const update = addDashboardUpdate(dashboard);
        this.updateConfiguration$.next(update);
    }

    public removeDashboard(dashboard: number) {
        const update = removeDashboardUpdate(dashboard);
        this.updateConfiguration$.next(update);
    }
}
